import { useEffect, useState } from 'react';
import type { GetStaticProps } from 'next';
import CrashCourseLandingPage from 'pages/CrashCourseLandingPage';
import type { CrashCourseArticle } from 'interfaces/content/articles/Articles';
import type { HeaderNav } from 'interfaces/navigation/Header';
import getFooter from 'api/Footer/getFooter';
import { getMicrositeConfig } from 'api/MicrositeConfig/getMicrositeConfig';
import { setPianoConfig } from 'services/Piano';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import type { FilterSection, FilterValue } from 'components/CrashCourseFilter/CrashCoursesFilterSection';
import setCryptoPageAdConfig from 'services/Ad/config/setCryptoPageAdConfig';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getCrashCoursesArticles from 'api/CrashCourse/getAllCrashCoursesArticles';
import { Microsites, crashCourseLandingPageFilters } from 'constants/constants';
import getMenu from 'api/Header/getMenu';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';
import { PianoExperiencePageType } from '@/app_lib/types/piano/Paywall';

interface LandingPageProps {
  crashCourseContent: CrashCourseArticle[];
  breadcrumbs: BreadcrumbsProps;
}

const LandingPage = ({ crashCourseContent, breadcrumbs }: LandingPageProps) => {
  const [cards, setCards] = useState<CrashCourseArticle[]>(crashCourseContent);
  const [filters, setFilters] = useState<FilterSection[]>(crashCourseLandingPageFilters);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    let isMounted = false;

    if (!isMounted) setCards(crashCourseContent);

    return () => {
      isMounted = true;
    };
  }, [crashCourseContent]);

  const filterSearchCards = (search: string | null, filter: { value: FilterValue; checked: boolean } | null) => {
    let searchTerm = searchValue;
    let filterValues = [...filters];

    if (filter !== null) {
      const newFilters = filters.map((fil) => {
        const newOptions = fil.options.map((option) => {
          if (option.value === filter.value[fil.key]) {
            return { ...option, checked: filter.checked };
          }
          return option;
        });
        return { ...fil, options: newOptions };
      });

      setFilters(newFilters);
      filterValues = [...newFilters];
    }
    if (search !== null) {
      setSearchValue(search);
      searchTerm = search;
    }

    const optionsList = filterValues?.flatMap((fil) => fil.options).filter((fil) => fil.checked);

    const newCards = crashCourseContent.filter(
      (card) =>
        (!optionsList?.[0] || optionsList.some((option) => option.value === card.complexity)) &&
        (!searchTerm ||
          card.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          card.cleanedContent.toLowerCase().includes(searchTerm.toLowerCase())),
    );

    setCards(newCards);
  };

  return (
    <CrashCourseLandingPage
      searchValue={searchValue}
      breadcrumbs={breadcrumbs}
      articles={cards}
      filters={filters}
      onSearch={(value: string) => filterSearchCards(value, null)}
      onInput={(val) => {
        if (!val) filterSearchCards('', null);
      }}
      onComplexityChange={(value: FilterValue, checked: boolean) => filterSearchCards(null, { checked, value })}
    />
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', Microsites.Crypto, null),
      fallbackData.headerNav,
    );

    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const crashCourseConfig = await safeAsync(
      () => getMicrositeConfig(Microsites.Crypto, 'crashCourseLanding'),
      fallbackData.pageData,
    );

    const crashCourseHomepage = crashCourseConfig.pageConfig!;
    const { breadcrumbs } = crashCourseConfig;
    const headData: HeadData = safe(
      () => setHeadData({ ...crashCourseHomepage, breadcrumbs }, true, 'Fortune Crypto'),
      fallbackData.headData,
    );
    const dataLayer = safe(() => setPageGtmConfig(crashCourseHomepage), fallbackData.dataLayer);

    const articles = (await getCrashCoursesArticles(100)) || [];

    return {
      props: {
        breadcrumbs,
        crashCourseContent: articles,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        pageAdConfig: setCryptoPageAdConfig(crashCourseHomepage),
        pianoPageConfig: setPianoConfig({ pageType: PianoExperiencePageType.Default, pageData: {} }),
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export default LandingPage;
